(() => {
  const d = document
  const w = window
  const selectAll = (sel, ctx = d) => Array.from(ctx.querySelectorAll(sel))
  const isProduct = w.location.pathname.indexOf('/products/') !== -1

  // ---------- Local & Session Storage helpers (with simple TTL for ZIP->Zone) ----------
  const LS = {
    ZIP: 'savedZip',
    ZONE: 'growZone',
    ZIP_CACHE: 'zoneByZipCache' // { [zip]: { z: "7b", t: 1712345678901 } }
  }
  const SS = {
    GEOLOCATION_ATTEMPTED: 'geolocationAttempted' // Track if user has already been prompted for geolocation in this session
  }
  const getCache = () => JSON.parse(localStorage.getItem(LS.ZIP_CACHE) || '{}')
  const setCache = (zip, zone) => {
    const c = getCache()
    c[zip] = { z: zone, t: Date.now() }
    localStorage.setItem(LS.ZIP_CACHE, JSON.stringify(c))
  }
  const getCachedZone = (zip, ttlMs = 30 * 24 * 3600 * 1000) => {
    const hit = getCache()[zip]
    return hit && (Date.now() - hit.t < ttlMs) ? hit.z : null
  }

  // ---------- Elements (lazy-queried when needed) ----------
  const els = {
    get modal() { return d.getElementById('growZoneModal') },
    get input() { return d.getElementById('growZoneInput') },
    get error() { return d.querySelector('.form__message--error') },
    get plpForm() { return d.getElementById('plpZipForm') },
    get plpZip() { return d.getElementById('PlpZip') },
    get plpMsg() { return d.getElementById('PlpZipMsg') }
  }

  // ---------- Fast event delegation ----------
  const actions = {
    setGrowZone() {
      const v = els.input?.value?.trim()
      if (!v) return
      getGrowZone(v)
      els.input.value = ''
    },
    setGrowZoneCurrent() {
      // Mark that geolocation is being attempted when user manually requests it
      markGeolocationAttempted()
      getGrowZone(null)
    }
  }

  d.addEventListener('click', (e) => {
    const a = e.target.closest('[data-action]')
    if (a && actions[a.dataset.action]) actions[a.dataset.action]()
    if (e.target?.id === 'PlpZip') initPlpForm() // ensure PLP handler bound
  })

  d.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && d.activeElement?.matches?.('#growZoneInput')) actions.setGrowZone()
  })

  d.addEventListener('DOMContentLoaded', init)

  // ---------- Init ----------
  async function init() {
    // keep error UI pristine
    els.input?.addEventListener('input', hideZipError, { passive: true })

    const savedZip = localStorage.getItem(LS.ZIP)
    const savedZone = localStorage.getItem(LS.ZONE)

    if (savedZip && savedZone) {
      setZoneTexts(savedZip, savedZone)
      if (isProduct) updatePDPgrowZone()
      //observeFiltersOnce(() => checkMatchingZone(savedZone))
    } else {
      // Only auto-geolocate if permission is already granted (non-blocking, faster perceived speed)
      const canAutoLocate = await geolocationGranted()
      if (canAutoLocate) {
        getGrowZone(null)
      } else if (!geolocationAlreadyAttempted()) {
        // Only prompt for geolocation if user hasn't been prompted in this session
        getGrowZone(null)
      }
      // If user has already been prompted for geolocation in this session, wait for them to manually enter ZIP or click "Use current location"
    }

    initPlpForm()
    if (!savedZip && !savedZone) {
      selectAll('.product__growing-zones [data-action="toggleZipModal"]').forEach(el => el.textContent = 'Enter Your Zip Code')
    }
  }

  // ---------- Permissions ----------
  async function geolocationGranted() {
    if (!('geolocation' in navigator)) return false
    
    // If Permissions API is not available (Safari < 16.4), check sessionStorage flag
    if (!('permissions' in navigator)) {
      return !geolocationAlreadyAttempted()
    }
    
    try {
      const p = await navigator.permissions.query({ name: 'geolocation' })
      return p.state === 'granted'
    } catch { 
      // Fallback: if Permissions API fails, check sessionStorage flag
      return !geolocationAlreadyAttempted()
    }
  }

  // Check if user has already been prompted for geolocation in this session
  function geolocationAlreadyAttempted() {
    return sessionStorage.getItem(SS.GEOLOCATION_ATTEMPTED) === 'true'
  }

  // Mark that geolocation has been attempted in this session
  function markGeolocationAttempted() {
    sessionStorage.setItem(SS.GEOLOCATION_ATTEMPTED, 'true')
  }

  // ---------- Fetch helpers ----------
  async function fetchWithTimeout(url, opts = {}, timeoutMs = 7000) {
    const ctrl = new AbortController()
    const id = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
      const res = await fetch(url, { ...opts, signal: ctrl.signal })
      return res
    } finally {
      clearTimeout(id)
    }
  }

  // 1) Get coords
  function getLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        showZipError('Geolocation is not supported by this browser.')
        return reject(new Error('Geolocation unsupported'))
      }
      
      // Mark that geolocation is being attempted
      markGeolocationAttempted()
      
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          resolve({ latitude: coords.latitude, longitude: coords.longitude })
        },
        (err) => {
          if (err.code === 1) {
            showZipError('Location access was denied. Please enable it for better service.')
          } else {
            showZipError('Could not retrieve your location.')
          }
          reject(err)
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      )
    })
  }

  // 2) Reverse geocode to ZIP (note: for production, proxy this server-side for reliability/policy)
  async function getZipFromCoords(lat, lon) {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1&accept-language=en`
    const res = await fetchWithTimeout(url, { headers: { 'Accept': 'application/json' } }, 7000)
    const data = await res.json().catch(() => ({}))
    const zip = data?.address?.country_code === 'us' ? data?.address?.postcode : null
    if (!zip) throw new Error('Cannot use location feature outside of the US, or error fetching zip code.')
    return zip
  }

  // 3) App endpoint: ZIP -> Zone (with cache)
  async function getZoneByZip(zip, saveLocal = true) {
    const cached = getCachedZone(zip)
    if (cached) {
      if (saveLocal) localStorage.setItem(LS.ZIP, zip)
      hideZipError()
      return { zone: cached }
    }
    try {
      const res = await fetchWithTimeout(`/apps/grove-zone?zip=${encodeURIComponent(zip)}`, {}, 7000)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (data?.zone) {
        setCache(zip, data.zone)
        if (saveLocal) localStorage.setItem(LS.ZIP, zip)
        hideZipError()
        return data
      }
      return null
    } catch (e) {
      console.error('Error fetching zone data:', e)
      return null
    }
  }

  // ---------- Orchestrator ----------
  async function getGrowZone(manualZip) {
    try {
      let zip = manualZip
      if (!zip) {
        const { latitude, longitude } = await getLocation()
        zip = await getZipFromCoords(latitude, longitude)
      }
      const data = await getZoneByZip(zip)
      if (!data?.zone) return showZipError('Please enter a valid zip code')

      // Apply changes in a single frame to reduce layout thrash
      requestAnimationFrame(() => {
        hideZipError()
        els.modal?.classList.remove('show-modal')
        d.body.classList.remove('overflow-hidden')
        
        els.modal?.setAttribute('aria-hidden', 'true')
        els.modal?.classList.add('invisible')

        if (window.removeTrapFocus) {
          window.removeTrapFocus()
        }

        setZoneTexts(zip, data.zone)
        localStorage.setItem(LS.ZONE, data.zone)
        dispatchZoneEvent(data.zone, zip)
        if (isProduct) updatePDPgrowZone()
        //observeFiltersOnce(() => checkMatchingZone(data.zone))
      })
    } catch (err) {
      console.error('Error:', err)
    }
  }

  // ---------- UI helpers ----------
  function showZipError(msg = 'Please enter a valid zip code') {
    els.input?.setAttribute('aria-invalid', 'true')
    if (els.error) {
      els.error.textContent = msg
      els.error.classList.remove('hidden')
    }
  }
  function hideZipError() {
    els.input?.setAttribute('aria-invalid', 'false')
    if (els.error) {
      els.error.textContent = 'Please enter a valid zip code'
      els.error.classList.add('hidden')
    }
  }

  function setZoneTexts(zip, zone) {
    selectAll('.js-growZone').forEach(el => {
      if (el.classList.contains('js-growZoneText')) el.textContent = `Growing Zone: ${zone}`
      else el.textContent = zone
    })
    selectAll('.js-zipText span').forEach(el => { el.textContent = String(zip) })
    selectAll('.js-zipText').forEach(el => { el.style.display = 'block' })

    if (isProduct) updatePDPgrowZone()
  }

  function dispatchZoneEvent(growZone, zip) {
    d.dispatchEvent(new CustomEvent('growZoneUpdated', { detail: { growZone, zip } }))
  }

  // ---------- PDP UI ----------
  function updatePDPgrowZone() {
    const zoneNumbers = (localStorage.getItem('zoneNumbersArray') || '').split(',').filter(Boolean)
    const growEls = selectAll('.js-growText')
    const zoneStatus = selectAll('.zone-status')
    const gz = localStorage.getItem(LS.ZONE)

    const setStatus = (inZone) => {
      growEls.forEach(el => el.textContent = inZone ? 'Good to Grow!' : 'May not be suitable for your region:')
      zoneStatus.forEach(el => el.classList.toggle('out-of-zone', !inZone))
      zoneStatus.forEach(el => el.classList.remove('hidden'))
      selectAll('.product__growing-zones [data-action="toggleZipModal"]').forEach(el => { el.textContent = 'Change Location' })
    }

    if (!gz) {
      growEls.forEach(el => (el.textContent = 'Enter Your Zip Code'))
      zoneStatus.forEach(el => el.classList.add('hidden'))
      return
    }

    const inZone = zoneNumbers.includes(gz.slice(0, -1))
    setStatus(inZone)
  }

  // ---------- Filters auto-match without setTimeout ----------
  function observeFiltersOnce(cb) {
    if ($('.blm-product-search-filter-item__checkbox')) return void cb()
    const mo = new MutationObserver(() => {
      if ($('.blm-product-search-filter-item__checkbox')) {
        mo.disconnect()
        cb()
      }
    })
    mo.observe(d.documentElement, { childList: true, subtree: true })
    function $(sel, ctx = d) { return ctx.querySelector(sel) }
  }

  function checkMatchingZone(growZone) {
    const zoneNumber = (growZone.match(/^(\d+)/) || [])[1]
    if (!zoneNumber) return
    selectAll('.blm-product-search-filter-item__checkbox').forEach((input) => {
      const m = input.value.match(/Zone\s+(\d+)/i)
      if (m && m[1] === zoneNumber && !input.checked) input.click()
    })
  }

  // ---------- PLP form ----------
  function initPlpForm() {
    const form = els.plpForm
    const zipInput = els.plpZip
    const msg = els.plpMsg
    if (!form || form.dataset.listenerAttached === 'true') return

    form.dataset.listenerAttached = 'true'
    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      const zip = zipInput?.value?.trim()
      if (!zip || !msg) return
      const data = await getZoneByZip(zip, false)
      msg.style.display = 'block'
      msg.textContent = data?.zone ? `The zip code you entered is in Zone ${data.zone}.` : 'Please enter a valid zip code'
    })
  }
})()
